import { Formik, FormikHelpers, useFormikContext } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import { AttributeFormikField } from '~/components/AttributeForm';
import { SubmitButton } from '~/components/form/SubmitButton';
import { Attachment } from '~/generated/graphql';
import { getFragmentData, graphql } from '~/gql';
import {
  AttributeFieldsFragment,
  getEntityType,
  HasAttributes,
} from './AttributesList';

export type AttributeEntityType = 'Asset' | 'Contact' | 'Item' | 'Site';

type FieldValues = {
  attributes: { id: string; value: string }[];
};

type Props = {
  entityId: string;
  data: HasAttributes;
  search?: string;
  onSuccess: () => void;
  onFormTouched?: (isDirty: boolean) => void;
};

const UpdateAttributeValues = graphql(`
  mutation UpdateAttributeValues($input: UpdateAttributeValuesInput!) {
    updateAttributeValues(input: $input) {
      ...AttributeFields
    }
  }
`);

export const AttributeEditForm = ({
  entityId,
  data,
  search,
  onSuccess,
  onFormTouched,
}: Props) => {
  const { t } = useTranslation();
  const [, update] = useMutation(UpdateAttributeValues);

  const entityType = getEntityType(entityId);
  const attributes = getFragmentData(AttributeFieldsFragment, data.attributes);

  const attachments = attributes?.reduce(
    (prev: Record<string, Attachment[]>, curr) => {
      if (curr.attachments && curr.attachments.length) {
        return { ...prev, [curr.id]: curr.attachments };
      }
      return prev;
    },
    {}
  );

  // TODO decouple from update site
  const initialValues: FieldValues = {
    attributes:
      attributes
        ?.filter(({ id, name }) => {
          // Never include integration-derived attributes; backend owns them
          if (id.startsWith('integration_')) return false;
          // Skip Guesty-sourced attributes (e.g., "Guesty \\ Listed", "Guesty Amenities \\ TV")
          // These are integration artifacts and should not be submitted in updates
          if (name && /^Guesty(\s|\\)/.test(name)) return false;
          return true;
        })
        ?.map(({ id, value }) => ({
          id,
          value: value.length ? value[0] : '',
        })) ?? [],
  };

  async function handleSubmit(
    data: FieldValues,
    { setSubmitting }: FormikHelpers<FieldValues>
  ) {
    try {
      // Filter out attributes with empty values - only send attributes that have actual values
      // The backend will remove attributes that aren't in this list
      const attributesToSend = data.attributes.filter((attr) => {
        const trimmed = attr.value?.trim() || '';
        return trimmed.length > 0;
      });

      const result = await update({
        input: {
          id: entityId,
          attributes: attributesToSend,
        },
      });

      if (result.error) {
        console.error('Failed to update attributes:', result.error);
        throw result.error;
      }

      // Brief delay to show the loading state, but let cache update immediately
      await new Promise((resolve) => setTimeout(resolve, 300));

      onSuccess();
    } catch (error) {
      console.error('Failed to update attributes:', error);
      // TODO: Add proper error handling/toast notification
      throw error;
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {({ dirty, isSubmitting }) => {
        return (
          <>
            {onFormTouched && <FormikDirtyEffect onDirty={onFormTouched} />}
            <div className='mx-4 mb-2 mt-2'>
              <AttributeFormikField
                entityType={entityType}
                attachments={attachments}
                search={search}
              />
            </div>

            {dirty && (
              <div className='fixed bottom-0 right-0 z-50 w-full border-t bg-white bg-opacity-80 p-4 shadow-lg backdrop-blur-md lg:w-rsb xl:w-2rsb'>
                <SubmitButton
                  loading={isSubmitting}
                  loadingText='Saving Changes...'
                >
                  {t('saveChanges')}
                </SubmitButton>
              </div>
            )}
          </>
        );
      }}
    </Formik>
  );
};

function FormikDirtyEffect(props: { onDirty: (dirty: boolean) => void }) {
  const { dirty } = useFormikContext();

  useEffect(() => {
    props.onDirty(dirty);
  }, [dirty]);

  return null;
}
